import {
    useEffect,
    type FC,
    useRef
} from "react";
import {
    View
} from "react-native";
import type IStatTileValueProps from "./type";
import stylesheet, {
    getStatTileValueDirection,
    getStatTileValueText,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useStatTileContext
} from "../../context";
import BadgeCounter from "../../../badge/internal/counter";
import Skeleton from "../../../skeleton";
import Text from "../../../text";

const StatTileValue: FC<IStatTileValueProps> = ({
    customTheme: customThemeProp,
    children,
    style,
    ...props
}) => {
    const context = useStatTileContext({
        part: "value"
    });

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        borders,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const text = getStatTileValueText({
        children
    });

    const previousTextRef = useRef(text);

    const direction = getStatTileValueDirection({
        previousText: previousTextRef.current,
        currentText: text
    });

    const {
        accessibleText: accessibleTextDynamicStyle
    } = useStyles({
        borders
    });

    useEffect(() => {
        previousTextRef.current = text;
    }, [
        text
    ]);

    if(!context) {
        return null;
    }

    if(context.isLoading) {
        return <Skeleton
            typography="headlineMediumSize"
            customTheme={customTheme}
            variant="text"
            width="60%"
        />;
    }

    if(text === undefined) {
        return <Text
            {...props}
            variant="headlineMediumSize"
            customTheme={customTheme}
            numberOfLines={1}
            style={style}
            color="high"
        >
            {children}
        </Text>;
    }

    return <View
        style={stylesheet.container}
    >
        <Text
            {...props}
            style={[
                stylesheet.accessibleText,
                accessibleTextDynamicStyle
            ]}
        >
            {text}
        </Text>
        <View
            aria-hidden={true}
        >
            <BadgeCounter
                customColor={colors.content.text.high}
                titleVariant="headlineMediumSize"
                customTheme={customTheme}
                direction={direction}
                titleStyle={style}
                title={text}
            />
        </View>
    </View>;
};
export default StatTileValue;
